/**
 * API client for Shield API Portal backend
 */

import type { AccessTokenGetter } from '@mideye/auth-react'
import { apiErrorMessage } from './apiErrorMessage'
import type {
  ApiKey,
  ApiKeyCreated,
  ApiKeyCreateRequest,
  ApiKeyListResponse,
  ApiKeyUsageInfo,
  OAuthConfig,
  HealthResponse,
  AuthConfig,
  UserInfo,
  EamComparison,
  EamEntraSource,
  EamPhoneSource,
  EamReadAccess,
  EamRemoveMatching,
  EamSignInApp,
  EamUser,
  EamUserList,
  EamView,
} from '../types/api'

const API_BASE = '/api/v1'

// Token getter function - will be set by the auth provider
let getAccessToken: AccessTokenGetter | null = null

/**
 * Set the function to get the access token
 */
export function setTokenGetter(getter: AccessTokenGetter | null) {
  getAccessToken = getter
}

export function clearAuthState() {
  setTokenGetter(null)
}

class ApiError extends Error {
  constructor(
    public status: number,
    public error: string,
    message: string,
    public details?: Record<string, unknown>
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      response.status,
      errorData.detail?.error || 'unknown_error',
      apiErrorMessage(errorData, response.statusText),
      errorData.detail?.details
    )
  }
  
  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T
  }
  
  return response.json()
}

/**
 * Get authorization headers for authenticated requests
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  if (!getAccessToken) {
    return {}
  }
  
  const token = await getAccessToken()
  if (!token) {
    return {}
  }
  
  return {
    Authorization: `Bearer ${token}`,
  }
}

// Cache for OAuth config - environment-wide, rarely changes
let oauthConfigCache: OAuthConfig | null = null

async function postSignInMove(action: 'finish' | 'cancel', appId: string): Promise<EamView> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/eam/realm/sign-in-app/${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({ app_id: appId }),
  })
  return handleResponse<EamView>(response)
}

export const api = {
  /**
   * Check API health (no auth required)
   */
  async health(): Promise<HealthResponse> {
    const response = await fetch(`${API_BASE}/health`)
    return handleResponse<HealthResponse>(response)
  },

  /**
   * Get auth configuration (no auth required). Returns the portal's Keycloak
   * OIDC configuration for the auth library to drive login.
   */
  async getAuthConfig(): Promise<AuthConfig> {
    const response = await fetch(`${API_BASE}/auth/config`)
    return handleResponse<AuthConfig>(response)
  },

  async getMe(): Promise<UserInfo> {
    const headers = await getAuthHeaders()
    const response = await fetch(`${API_BASE}/auth/me`, { headers })
    return handleResponse<UserInfo>(response)
  },

  /**
   * Clear the backend session (no auth required). Stateless 204 target wired
   * into the auth provider's logout flow via `clearBackendSession`.
   */
  async logout(): Promise<void> {
    const response = await fetch(`${API_BASE}/auth/logout`, { method: 'POST' })
    return handleResponse<void>(response)
  },

  /**
   * Get OAuth configuration for client credentials flow (no auth required, cached)
   */
  async getOAuthConfig(): Promise<OAuthConfig> {
    if (oauthConfigCache) {
      return oauthConfigCache
    }
    const response = await fetch(`${API_BASE}/oauth/config`)
    oauthConfigCache = await handleResponse<OAuthConfig>(response)
    return oauthConfigCache
  },

  /**
   * List all API keys for the user's company
   */
  async listApiKeys(): Promise<ApiKeyListResponse> {
    const headers = await getAuthHeaders()
    const response = await fetch(`${API_BASE}/api-keys`, { headers })
    return handleResponse<ApiKeyListResponse>(response)
  },

  /**
   * Get a specific API key
   */
  async getApiKey(keyId: string): Promise<ApiKey> {
    const headers = await getAuthHeaders()
    const response = await fetch(`${API_BASE}/api-keys/${keyId}`, { headers })
    return handleResponse<ApiKey>(response)
  },

  /**
   * Get usage information for an API key (combines cached OAuth config with client_id)
   */
  async getApiKeyUsage(clientId: string): Promise<ApiKeyUsageInfo> {
    const oauthConfig = await this.getOAuthConfig()
    return {
      client_id: clientId,
      ...oauthConfig,
    }
  },

  /**
   * Create a new API key
   */
  async createApiKey(request: ApiKeyCreateRequest): Promise<ApiKeyCreated> {
    const authHeaders = await getAuthHeaders()
    const response = await fetch(`${API_BASE}/api-keys`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(request),
    })
    return handleResponse<ApiKeyCreated>(response)
  },

  /**
   * Delete an API key
   */
  async deleteApiKey(keyId: string): Promise<void> {
    const authHeaders = await getAuthHeaders()
    const response = await fetch(`${API_BASE}/api-keys/${keyId}`, {
      method: 'DELETE',
      headers: authHeaders,
    })
    return handleResponse<void>(response)
  },

  /** The state of the company's Microsoft Entra MFA realm. */
  async getEam(): Promise<EamView> {
    const headers = await getAuthHeaders()
    const response = await fetch(`${API_BASE}/eam`, { headers })
    return handleResponse<EamView>(response)
  },

  async createEamRealm(tenantId: string, phoneSource: EamPhoneSource, signInApp: EamSignInApp): Promise<EamView> {
    const authHeaders = await getAuthHeaders()
    const response = await fetch(`${API_BASE}/eam/realm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ tenant_id: tenantId, phone_source: phoneSource, sign_in_app: signInApp }),
    })
    return handleResponse<EamView>(response)
  },

  async finishEamRealm(): Promise<EamView> {
    const authHeaders = await getAuthHeaders()
    const response = await fetch(`${API_BASE}/eam/realm/finish`, { method: 'POST', headers: authHeaders })
    return handleResponse<EamView>(response)
  },

  async setEamPhoneSource(source: EamPhoneSource): Promise<EamView> {
    const authHeaders = await getAuthHeaders()
    const response = await fetch(`${API_BASE}/eam/realm/phone-source`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ phone_source: source }),
    })
    return handleResponse<EamView>(response)
  },

  async saveEamMethodId(methodId: string): Promise<EamView> {
    const authHeaders = await getAuthHeaders()
    const response = await fetch(`${API_BASE}/eam/realm/method-id`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ method_id: methodId }),
    })
    return handleResponse<EamView>(response)
  },

  async saveEamSignInApp(app: EamSignInApp, appId?: string): Promise<EamView> {
    const authHeaders = await getAuthHeaders()
    const response = await fetch(`${API_BASE}/eam/realm/sign-in-app`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(appId === undefined ? { app } : { app, app_id: appId }),
    })
    return handleResponse<EamView>(response)
  },

  async finishEamSignInMove(appId: string): Promise<EamView> {
    return postSignInMove('finish', appId)
  },

  async cancelEamSignInMove(appId: string): Promise<EamView> {
    return postSignInMove('cancel', appId)
  },

  async listEamUsers(): Promise<EamUserList> {
    const headers = await getAuthHeaders()
    const response = await fetch(`${API_BASE}/eam/users`, { headers })
    return handleResponse<EamUserList>(response)
  },

  async getEamReadAccess(): Promise<EamReadAccess> {
    const headers = await getAuthHeaders()
    const response = await fetch(`${API_BASE}/eam/read-access`, { headers })
    return handleResponse<EamReadAccess>(response)
  },

  async compareEamUsers(source: EamEntraSource): Promise<EamComparison> {
    const headers = await getAuthHeaders()
    const response = await fetch(`${API_BASE}/eam/users/comparison?source=${encodeURIComponent(source)}`, { headers })
    return handleResponse<EamComparison>(response)
  },

  async removeMatchingEamUsers(): Promise<EamRemoveMatching> {
    const headers = await getAuthHeaders()
    const response = await fetch(`${API_BASE}/eam/users/remove-matching`, { method: 'POST', headers })
    return handleResponse<EamRemoveMatching>(response)
  },

  async addEamUser(objectId: string, phone: string): Promise<EamUser> {
    const authHeaders = await getAuthHeaders()
    const response = await fetch(`${API_BASE}/eam/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ object_id: objectId, phone }),
    })
    return handleResponse<EamUser>(response)
  },

  async changeEamUser(id: string, phone: string): Promise<EamUser> {
    const authHeaders = await getAuthHeaders()
    const response = await fetch(`${API_BASE}/eam/users/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ phone }),
    })
    return handleResponse<EamUser>(response)
  },

  async removeEamUser(id: string): Promise<void> {
    const headers = await getAuthHeaders()
    const response = await fetch(`${API_BASE}/eam/users/${encodeURIComponent(id)}`, { method: 'DELETE', headers })
    return handleResponse<void>(response)
  },
}

export { ApiError }
